import React from 'react';

const SLIDERS = [
    {key: 'genre_exact', label: 'Same genre', min: 0, max: 0.9, step: 0.01, fmt: (v) => `closes ${Math.round(v * 100)}% of the gap`},
    {key: 'genre_family', label: 'Same genre family', min: 0, max: 0.9, step: 0.01, fmt: (v) => `closes ${Math.round(v * 100)}% of the gap`},
    {key: 'era', label: 'Same era', min: 0, max: 0.9, step: 0.01, fmt: (v) => `up to ${Math.round(v * 100)}% of the gap`},
    {key: 'era_half_life', label: 'Era half-life', min: 1, max: 30, step: 1, fmt: (v) => `bonus halves every ${v} years`},
];

const Toggle = ({on, onClick}) => (
    <button type="button" onClick={onClick}
            className={'relative h-[18px] w-8 flex-none rounded-full transition ' + (on ? 'bg-apple-red' : 'bg-white/20')}>
        <span className={'absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all ' + (on ? 'left-[16px]' : 'left-[2px]')}/>
    </button>
);

/**
 * The scoring knobs. Changes are live and local (onChange); saving makes
 * them the default for the app and the API.
 */
export default function TuningPanel({values, saved, onChange, onSave, onRevert, busy, header, footer, saveLabel = 'Save as default'}) {
    if (!values) {
        return null;
    }
    const set = (k, v) => onChange({...values, [k]: v});
    const dirty = saved && JSON.stringify(values) !== JSON.stringify(saved);

    return (
        <div className="rounded-2xl bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">Scoring</span>
                {dirty && <span className="rounded-full bg-amber-400/[0.15] px-2 py-0.5 text-[9.5px] font-extrabold uppercase text-amber-300">Unsaved</span>}
            </div>
            {header}
            <div className="mt-3 space-y-3.5">
                {SLIDERS.map((s) => (
                    <label key={s.key} className="block">
                        <span className="flex items-baseline justify-between text-[12px]">
                            <span className="font-semibold text-white/80">{s.label}</span>
                            <span className="text-[11px] tabular-nums text-white/40">{s.fmt(values[s.key])}</span>
                        </span>
                        <input type="range" min={s.min} max={s.max} step={s.step} value={values[s.key]}
                               onChange={(e) => set(s.key, parseFloat(e.target.value))}
                               className="mt-1.5 h-1 w-full cursor-pointer appearance-none rounded-full bg-white/[0.15] accent-apple-red"/>
                    </label>
                ))}
                <div className="flex items-center justify-between text-[12px]">
                    <span className="font-semibold text-white/80">Genre lock</span>
                    <Toggle on={values.genre_lock} onClick={() => set('genre_lock', !values.genre_lock)}/>
                </div>
                <div className="flex items-center justify-between gap-3 text-[12px]">
                    <span className="font-semibold text-white/80">Era lock</span>
                    <span className="flex items-center gap-2">
                        <span className="text-[11px] text-white/40">±</span>
                        <input type="number" min={0} max={50} value={values.era_years}
                               onChange={(e) => set('era_years', parseInt(e.target.value || '0', 10))}
                               className="w-12 rounded-md bg-white/[0.08] px-1.5 py-0.5 text-right text-[11.5px] tabular-nums outline-none"/>
                        <span className="text-[11px] text-white/40">yrs</span>
                        <Toggle on={values.era_lock} onClick={() => set('era_lock', !values.era_lock)}/>
                    </span>
                </div>
            </div>
            <p className="mt-4 text-[10.5px] leading-relaxed text-white/40">
                Score = sound (all 84 values together, corrected for crowding) + genre + era bonuses.
                Saving applies to the app and the API within a minute, no deploy needed.
            </p>
            <div className="mt-3 flex gap-2">
                <button type="button" disabled={!dirty || busy} onClick={onRevert}
                        className="flex-1 rounded-xl bg-white/[0.08] py-2 text-[12px] font-semibold text-white/70 transition hover:text-white disabled:opacity-30">
                    Revert
                </button>
                <button type="button" disabled={!dirty || busy}
                        onClick={() => window.confirm(saveLabel + '? It applies to the app and the API.') && onSave()}
                        className="flex-1 rounded-xl bg-apple-red py-2 text-[12px] font-bold transition disabled:opacity-30">
                    {saveLabel}
                </button>
            </div>
            {footer}
        </div>
    );
}
